import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { billingMessages } from 'src/features/billing/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { Link, useParams } from 'react-router-dom';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { commonMessages, generalMessages } from 'src/utils/messages';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { customersMessages } from 'src/features/customers/utils/messages';
import { useGetCustomer } from 'src/features/customers/api/useGetCustomer';
import UpdateCustomerFormModal from 'src/features/customers/components/forms/UpdateCustomerFormModal';
import DeleteCustomerFormModal from 'src/features/customers/components/forms/DeleteCustomerFormModal';
import { LANGUAGE_TRANSLATION_MAP, PAYMENT_PROVIDER_TRANSLATION_MAP } from 'src/features/customers/utils/constants';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import CustomerDashboardTables from './CustomerDashboardTables';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const CustomerDashboard = () => {
    const { formatMessage } = useIntl();
    const { customerId = '' } = useParams();

    const permissionManageCustomers = useHasPermissions([PERMISSION.MANAGE_CUSTOMERS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(customersMessages.customerDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: customerData } = useGetCustomer({
        id: customerId,
        config: { enabled: !!customerId },
    });

    const dashboardBlockData = {
        section1: [
            {
                title: billingMessages.legalName,
                value: customerData?.name ?? '-',
            },
            {
                title: billingMessages.legalNumber,
                value: customerData?.uic ?? '-',
            },
            {
                title: billingMessages.taxNumber,
                value: customerData?.tax_registration_number ?? '-',
            },
            {
                title: billingMessages.legalRepresentative,
                value: customerData?.representative ?? '-',
            },
            {
                title: commonMessages.email,
                value: customerData?.email && customerData.email !== '' ? customerData.email : '-',
            },
            {
                title: commonMessages.phone,
                value: customerData?.phone ?? '-',
            },
        ],
        section2: [
            {
                title: commonMessages.address,
                value: customerData?.address ?? '-',
            },
            {
                title: commonMessages.city,
                value: customerData?.city ?? '-',
            },
            {
                title: commonMessages.country,
                value: customerData?.country ?? '-',
            },
        ],
        section3: [
            {
                title: billingMessages.tenantId,
                value: customerData?.tenant?.id ?? '-',
            },
            {
                title: customersMessages.externalCustomerId,
                value: customerData?.external_customer_id ?? '-',
            },
        ],
        section4: [
            {
                title: commonMessages.currency,
                value: customerData?.configuration?.currency ? (
                    <CellCurrencyBadge currency={customerData?.configuration?.currency} />
                ) : (
                    '-'
                ),
            },
            {
                title: customersMessages.paymentProviderType,
                value: customerData?.configuration?.payment_provider
                    ? formatMessage(PAYMENT_PROVIDER_TRANSLATION_MAP[customerData?.configuration?.payment_provider])
                    : '-',
            },
            {
                title: customersMessages.paymentProviderCustomerId,
                value: 'TODO: add once we have it',
            },
            {
                title: commonMessages.language,
                value: customerData?.language ? formatMessage(LANGUAGE_TRANSLATION_MAP[customerData?.language]) : '-',
            },
            {
                title: customersMessages.gracePeriod,
                value: customerData?.configuration?.invoice_grace_period_days
                    ? formatMessage(commonMessages.days, {
                          value: customerData?.configuration?.invoice_grace_period_days,
                      })
                    : '-',
            },
        ],
    };

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link">
                <Link to={`/customers`}>
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(customersMessages.backToAllCustomers)}
                </Link>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {customerData?.name}
                        </SectionBlockTitle>

                        {permissionManageCustomers && customerData && (
                            <div className="flex flex-row gap-2">
                                <UpdateCustomerFormModal
                                    selectedCustomer={customerData}
                                    semantics={{ entryName: formatMessage(customersMessages.customer).toLowerCase() }}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                    </Button>
                                </UpdateCustomerFormModal>
                                <DeleteCustomerFormModal
                                    selectedCustomer={customerData}
                                    semantics={{ entryName: formatMessage(customersMessages.customer).toLowerCase() }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeleteCustomerFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={3} />
                </SectionBlock>
                <CustomerDashboardTables />
            </div>
        </>
    );
};

export default CustomerDashboard;
